/**
 * Үлдэгдэл размераар (`/accounting/stock-by-size`) — хэд хэдэн размер
 * сонгож салбар тус бүрийн ширхэг, зарах үнийн дүн, өртгийг харна.
 *
 * ⚠️ Өртөг тодорхойгүй гутал (дүрэм 4 — ханшаар бодохгүй) «өртөггүй»
 *    гэж тусдаа тоологдоно; өртгийн нийлбэр тэднийг ОРУУЛААГҮЙ.
 */

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { formatMnt, formatSize, money } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { DataTable } from '../data-table';
import { Section } from '../section';
import { StatCard, StatGrid } from '../stat-card';
import { Chip, Empty, ErrorNote } from '../ui';
import { AccountingFilters, ExcelButton, saveTableXlsx, useAccountingFilter } from './shared';

interface Cell {
  size: number | null;
  store: { id: string; name: string } | null;
  count: number;
  priceMnt: string;
  costMnt: string;
  uncosted: number;
}


export function StockBySizeTab() {
  const f = useAccountingFilter();
  const [params, setParams] = useSearchParams();
  const picked = (params.get('sizes') ?? '').split(',').filter(Boolean).map(Number);

  const toggle = (size: number) => {
    const next = picked.includes(size) ? picked.filter((s) => s !== size) : [...picked, size].sort((a, b) => a - b);
    const p = new URLSearchParams(params);
    if (next.length) p.set('sizes', next.join(','));
    else p.delete('sizes');
    setParams(p, { replace: true });
  };

  // Бүх размерыг НЭГ удаа татна: чип бүр үлдэгдэлтэй размер + тоотой,
  // сонголт нь клиент дээр шүүнэ (дахин хүсэлтгүй).
  const query = f.storeId ? `?storeId=${f.storeId}` : '';
  const q = useQuery({
    queryKey: ['accounting', 'stock-by-size', query],
    queryFn: () => api<Envelope<Cell[]>>(`/accounting/stock-by-size${query}`),
  });
  const all = q.data?.data ?? [];
  const sizes = [...all.reduce((m, c) => m.set(c.size, (m.get(c.size) ?? 0) + c.count), new Map<number | null, number>())]
    .sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0));
  const rows = picked.length ? all.filter((c) => c.size !== null && picked.includes(c.size)) : all;

  const sum = (pick: (c: Cell) => string) => rows.reduce((a, c) => a.plus(pick(c)), money(0)).toFixed(2);
  const count = rows.reduce((a, c) => a + c.count, 0);
  const uncosted = rows.reduce((a, c) => a + c.uncosted, 0);

  const excel = () =>
    saveTableXlsx({
      file: `uldegdel-razmer.xlsx`,
      title: 'Үлдэгдэл размераар',
      period: picked.length ? `Размер: ${picked.join(', ')}` : 'Бүх размер',
      headers: ['Размер', 'Салбар', 'Ширхэг', 'Зарах үнэ ₮', 'Өртөг ₮', 'Өртөггүй'],
      widths: [9, 24, 9, 16, 16, 10],
      rows: [
        ...rows.map((c) => [formatSize(c.size), c.store?.name ?? '—', c.count, { money: c.priceMnt }, { money: c.costMnt }, c.uncosted]),
        ['Нийт', null, count, { money: sum((c) => c.priceMnt) }, { money: sum((c) => c.costMnt) }, uncosted],
      ],
    });

  return (
    <Section
      title="Үлдэгдэл размераар"
      note="Нөөцөд = бэлэн ба буцаагдаад тавиурт ирсэн гутал. Өртөггүй гутал өртгийн дүнд ороогүй."
      actions={<ExcelButton onClick={excel} disabled={rows.length === 0} />}
    >
      <AccountingFilters f={f} dates={false} />
      <div className="flex flex-wrap gap-1.5">
        {sizes.map(([size, n]) =>
          size === null ? null : (
            <Chip key={size} active={picked.includes(size)} onClick={() => toggle(size)}>
              {size} <span className="num text-ink-3">· {n}</span>
            </Chip>
          ),
        )}
      </div>
      {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
      {rows.length > 0 ? (
        <StatGrid>
          <StatCard label="Ширхэг" value={String(count)} />
          <StatCard label="Зарах үнийн дүн" value={formatMnt(sum((c) => c.priceMnt))} />
          <StatCard label="Өртөг" value={formatMnt(sum((c) => c.costMnt))} footer={uncosted ? `${uncosted} ширхэг өртөггүй` : undefined} />
        </StatGrid>
      ) : null}
      <DataTable
        rows={rows}
        loading={q.isPending}
        rowKey={(c) => `${c.size}|${c.store?.id}`}
        empty={<Empty>Сонгосон размерт үлдэгдэл алга</Empty>}
        columns={[
          { key: 'size', header: 'Размер', mobile: 'title', cell: (c) => formatSize(c.size) },
          { key: 'store', header: 'Салбар', cell: (c) => c.store?.name ?? '—' },
          { key: 'count', header: 'Ширхэг', align: 'right', mobile: 'value', cell: (c) => c.count },
          { key: 'price', header: 'Зарах үнэ', align: 'right', nowrap: true, cell: (c) => formatMnt(c.priceMnt) },
          { key: 'cost', header: 'Өртөг', align: 'right', nowrap: true, cell: (c) => formatMnt(c.costMnt) },
          { key: 'uncosted', header: 'Өртөггүй', align: 'right', cell: (c) => c.uncosted || '—' },
        ]}
      />
    </Section>
  );
}
